import {
  redirectIfLoggedOut,
  api as apiMiddleware,
  updateSourceChangesToProgram,
  updateProgramLayers,
  sourceUpdateStatusManager,
  programEmitter,
  streamRecordStatus,
  presentationControlNotifier,
  showAPIError,
  setSourceInitializing,
  actionDebugger,
  videoPriorityBasedPolicyMiddleware,
  chimeMetricsOnstageAttendeeUpdaterMiddleware,
  attendeeRosterEmitter,
  toggleBackgroundHiddenMiddleware
} from '@shoflo/studio-redux/middleware';
import { createStore, applyMiddleware, compose } from 'redux';
import { routerMiddleware } from 'connected-react-router';
import { withExtraArgument } from 'redux-thunk';
import { createBrowserHistory } from 'history';
import createRootReducer from 'reducers';
import { setProgram } from 'actions/Broadcast';
import { getEnvVar } from '@shoflo/polymer';
import programSceneLayerUpdaterMiddleware from 'middleware/programSceneLayerUpdater';
import updateStaleProgramMiddleware from 'middleware/updateStaleProgram';
import joinMeetingMiddleware from 'middleware/joinMeeting';
import acceptedKnock from 'middleware/acceptedKnock';
import bulkAcceptedKnock from 'middleware/bulkAcceptedKnock';
import { setAccessTokenFromQueryString } from 'actions/utils';
import SocketDispatcher from './SocketDispatcher';
import broadcastSocketManager from 'middleware/broadcastSocketManager';
import rejectedKnockManager from './subscribers/rejectedKnockManager';
import attendeeCameraStatusEmitter from './subscribers/attendeeCameraStatusEmitter';
import { initialProgramState } from '@shoflo/studio-redux/reducers';
import programPresenterBannersUpdater from 'middleware/programPresenterBannersUpdater';
import addAudioPresenterLayerToProgramMiddleware from 'middleware/addAudioPresenterLayerToProgram';
import renewMeetingStreamMiddleware from 'middleware/renewMeetingStream';
import sli from 'middleware/sli';
import notifyDirectors from 'middleware/notifyDirectors';
import {
  BROADCAST_START_RECORDING,
  BROADCAST_START_RECORDINGS,
  BROADCAST_STOP_RECORDING,
  BROADCAST_STOP_RECORDINGS
} from 'actions/Types';

export const defaultInitialState = {
  program: initialProgramState
};

function createConfiguredStore(initialState = defaultInitialState) {
  setAccessTokenFromQueryString(window, document);

  const socketDispatcher = new SocketDispatcher();
  const socket = socketDispatcher.connect();

  const history = createBrowserHistory();

  const enhancers = [];
  const middleware = [
    withExtraArgument(socket),
    routerMiddleware(history),
    apiMiddleware,
    sli([
      `${BROADCAST_START_RECORDING}_SUCCESS`,
      `${BROADCAST_START_RECORDINGS}_SUCCESS`,
      `${BROADCAST_START_RECORDING}_FAILURE`,
      `${BROADCAST_START_RECORDINGS}_FAILURE`,
      `${BROADCAST_STOP_RECORDING}_SUCCESS`,
      `${BROADCAST_STOP_RECORDINGS}_SUCCESS`,
      `${BROADCAST_STOP_RECORDING}_FAILURE`,
      `${BROADCAST_STOP_RECORDINGS}_FAILURE`
    ]),
    programSceneLayerUpdaterMiddleware,
    broadcastSocketManager(socket),
    updateStaleProgramMiddleware,
    programEmitter(socket),
    programPresenterBannersUpdater,
    notifyDirectors(socket),
    acceptedKnock(socket),
    bulkAcceptedKnock(socket),
    addAudioPresenterLayerToProgramMiddleware,
    streamRecordStatus(socket),
    redirectIfLoggedOut,
    showAPIError,
    updateSourceChangesToProgram(setProgram),
    renewMeetingStreamMiddleware(socket),
    updateProgramLayers(),
    presentationControlNotifier(),
    sourceUpdateStatusManager(),
    setSourceInitializing,
    videoPriorityBasedPolicyMiddleware,
    chimeMetricsOnstageAttendeeUpdaterMiddleware,
    joinMeetingMiddleware,
    attendeeRosterEmitter(socket),
    toggleBackgroundHiddenMiddleware(),
    actionDebugger // this should be the last middleware
  ];

  if (getEnvVar('NODE_ENV') === 'development') {
    const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;

    if (typeof devToolsExtension === 'function') {
      enhancers.push(devToolsExtension());
    }
  }

  const composedEnhancers = compose(
    applyMiddleware(...middleware),
    ...enhancers
  );

  const store = createStore(
    createRootReducer(history),
    initialState,
    composedEnhancers
  );

  socketDispatcher.bindToStore(store);

  store.subscribe(rejectedKnockManager.bind({ store, socket }));
  store.subscribe(attendeeCameraStatusEmitter.bind({ store, socket }));

  return {
    store,
    history
  };
}

export { createConfiguredStore };
